import React, { useState, useMemo } from 'react';
import { Play, Users, TrendingUp, Clock, Eye, Calendar, X } from 'lucide-react';
import { formatFullNumber, formatDate, getPlatformColor } from '../utils/formatters';
import YouTubeEstimateTooltip from './YouTubeEstimateTooltip';
import { isYouTubeDataStale } from '../utils/youtubeCompliance';

// API base URL for thumbnail resolution
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Cache version - increment this when thumbnails are updated to bust browser cache
const CACHE_VERSION = 'v3';

// Helper to resolve thumbnail URLs (local cached thumbnails start with /api/)
const resolveThumbnailUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    const base = window.location.hostname === 'localhost' ? API_BASE_URL : '';
    return `${base}${url}?${CACHE_VERSION}`;
  }
  return url;
};

// Format watch hours
const formatWatchHours = (hours) => {
  if (!hours) return '0';
  if (hours >= 1000) return (hours / 1000).toFixed(1) + 'K';
  return Math.round(hours).toLocaleString();
};

const StreamingGridView = ({ content, reportId, onContentExcluded, excludedContentIds = [], sortBy: controlledSortBy, onSortChange }) => {
  const [internalSortBy, setInternalSortBy] = useState('avgCcv-high');
  const sortBy = controlledSortBy !== undefined ? controlledSortBy : internalSortBy;
  const setSortBy = onSortChange || setInternalSortBy;
  const [filterCreator, setFilterCreator] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [itemsToShow, setItemsToShow] = useState(10);
  const [removing, setRemoving] = useState(null);
  const [toast, setToast] = useState(null);

  // Get unique creators and platforms
  const creators = useMemo(() => {
    const unique = [...new Set(content.map(c => c.creator?.id))].filter(Boolean);
    return unique.map(id => content.find(c => c.creator?.id === id)?.creator).filter(Boolean);
  }, [content]);

  const platforms = useMemo(() => {
    return [...new Set(content.map(c => c.platform))].filter(Boolean).sort();
  }, [content]);

  // Handle content exclusion
  const handleExcludeContent = async (contentId) => {
    if (!reportId) return;

    setRemoving(contentId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/client-reports/${reportId}/exclude-content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ contentId })
      });

      if (!response.ok) {
        setToast({ message: `Failed to remove content (${response.status})`, type: 'error' });
        setTimeout(() => setToast(null), 3000);
        return;
      }

      const data = await response.json();
      setToast({ message: 'Content removed from report', action: 'Undo', onUndo: () => handleRestoreContent(contentId) });
      setTimeout(() => setToast(null), 5000);

      if (onContentExcluded) {
        onContentExcluded(data);
      }
    } catch (error) {
      setToast({ message: 'Failed to remove content: ' + error.message, type: 'error' });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setRemoving(null);
    }
  };

  const handleRestoreContent = async (contentId) => {
    if (!reportId) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/client-reports/${reportId}/restore-content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ contentId })
      });

      if (response.ok) {
        const data = await response.json();
        setToast(null);
        if (onContentExcluded) {
          onContentExcluded(data);
        }
      }
    } catch (error) {
      console.error('Error restoring content:', error);
    }
  };

  // Sort and filter content
  const filteredContent = useMemo(() => {
    let filtered = [...content];

    if (filterCreator !== 'all') {
      filtered = filtered.filter(c => c.creator?.id === filterCreator);
    }
    if (filterPlatform !== 'all') {
      filtered = filtered.filter(c => c.platform === filterPlatform);
    }

    // Sort by streaming metrics
    filtered.sort((a, b) => {
      const aAvgCcv = a.streamAvgCcv || a.avgCcv || 0;
      const bAvgCcv = b.streamAvgCcv || b.avgCcv || 0;
      const aPeakCcv = a.streamPeakCcv || a.peakCcv || 0;
      const bPeakCcv = b.streamPeakCcv || b.peakCcv || 0;
      const aWatchHours = a.streamWatchHours || a.watchHours || a.watchTimeHours || 0;
      const bWatchHours = b.streamWatchHours || b.watchHours || b.watchTimeHours || 0;
      const aVodViews = a.vodViews || 0;
      const bVodViews = b.vodViews || 0;

      switch (sortBy) {
        case 'avgCcv-high': return bAvgCcv - aAvgCcv;
        case 'avgCcv-low': return aAvgCcv - bAvgCcv;
        case 'peakCcv-high': return bPeakCcv - aPeakCcv;
        case 'peakCcv-low': return aPeakCcv - bPeakCcv;
        case 'watchHours-high': return bWatchHours - aWatchHours;
        case 'watchHours-low': return aWatchHours - bWatchHours;
        case 'vodViews-high': return bVodViews - aVodViews;
        case 'vodViews-low': return aVodViews - bVodViews;
        case 'date-new': return new Date(b.postDate) - new Date(a.postDate);
        case 'date-old': return new Date(a.postDate) - new Date(b.postDate);
        default: return bAvgCcv - aAvgCcv;
      }
    });

    return filtered;
  }, [content, filterCreator, filterPlatform, sortBy]);

  return (
    <div className="space-y-4">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-3 ${
          toast.type === 'error' ? 'bg-red-600' : 'bg-gray-800'
        } text-white`}>
          <span>{toast.message}</span>
          {toast.action && toast.onUndo && (
            <button onClick={toast.onUndo} className="font-medium underline hover:no-underline">
              {toast.action}
            </button>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="rounded-lg p-4 space-y-4 bg-gray-900 border border-white shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Sort By */}
          <div>
            <label className="block text-xs font-medium text-white mb-2">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(75,85,99,1)' }}
            >
              <optgroup label="Average CCV">
                <option value="avgCcv-high">Highest Avg CCV</option>
                <option value="avgCcv-low">Lowest Avg CCV</option>
              </optgroup>
              <optgroup label="Peak CCV">
                <option value="peakCcv-high">Highest Peak CCV</option>
                <option value="peakCcv-low">Lowest Peak CCV</option>
              </optgroup>
              <optgroup label="Watch Hours">
                <option value="watchHours-high">Most Watch Hours</option>
                <option value="watchHours-low">Least Watch Hours</option>
              </optgroup>
              <optgroup label="VOD Views">
                <option value="vodViews-high">Most VOD Views</option>
                <option value="vodViews-low">Least VOD Views</option>
              </optgroup>
              <optgroup label="Date">
                <option value="date-new">Newest First</option>
                <option value="date-old">Oldest First</option>
              </optgroup>
            </select>
          </div>

          {/* Creator Filter */}
          <div>
            <label className="block text-xs font-medium text-white mb-2">Creator</label>
            <select
              value={filterCreator}
              onChange={(e) => setFilterCreator(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(75,85,99,1)' }}
            >
              <option value="all">All Creators</option>
              {creators.map(creator => (
                <option key={creator.id} value={creator.id}>{creator.name}</option>
              ))}
            </select>
          </div>

          {/* Platform Filter */}
          <div>
            <label className="block text-xs font-medium text-white mb-2">Platform</label>
            <select
              value={filterPlatform}
              onChange={(e) => setFilterPlatform(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(75,85,99,1)' }}
            >
              <option value="all">All Platforms</option>
              {platforms.map(platform => (
                <option key={platform} value={platform}>
                  {{ youtube: 'YouTube', tiktok: 'TikTok', twitch: 'Twitch', kick: 'Kick', twitter: 'Twitter', instagram: 'Instagram' }[platform?.toLowerCase()] || platform}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Results Summary */}
        <div className="text-sm text-white">
          Showing <span className="font-medium text-white">{filteredContent.length}</span> of <span className="font-medium text-white">{content.length}</span> streams
        </div>
      </div>

      {/* Grid of Stream Cards */}
      {filteredContent.length > 0 ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filteredContent.slice(0, itemsToShow).map(stream => (
              <div
                key={stream.id}
                className="rounded-lg overflow-hidden hover:shadow-xl transition-all duration-200 flex flex-col bg-gray-900 border border-white shadow-lg"
              >
                {/* Thumbnail Container */}
                <div className="relative bg-gray-900 aspect-video overflow-hidden group">
                  {stream.thumbnailUrl ? (
                    <img
                      src={resolveThumbnailUrl(stream.thumbnailUrl)}
                      alt={stream.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      onError={(e) => {
                        console.error('Image failed to load:', stream.thumbnailUrl, e);
                        e.target.style.display = 'none';
                        e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                      }}
                    />
                  ) : null}
                  <div
                    className={`w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 items-center justify-center ${stream.thumbnailUrl ? 'hidden' : 'flex'}`}
                  >
                    <Play className="w-8 h-8 text-white" />
                  </div>

                  {/* Platform Badge */}
                  <div className="absolute top-2 right-2">
                    <span
                      className="px-2 py-0.5 rounded text-xs font-medium text-white"
                      style={{ backgroundColor: getPlatformColor(stream.platform) + 'dd' }}
                    >
                      {{ youtube: 'YouTube', tiktok: 'TikTok', twitch: 'Twitch', kick: 'Kick', twitter: 'Twitter', instagram: 'Instagram' }[stream.platform?.toLowerCase()] || stream.platform}
                    </span>
                  </div>

                  {/* Creator Avatar */}
                  <div className="absolute top-2 left-2">
                    {stream.creator?.profileImageUrl ? (
                      <img
                        src={stream.creator.profileImageUrl}
                        alt={stream.creator.name}
                        className="w-6 h-6 rounded-full border-2 border-white object-cover"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-purple-500 border-2 border-white flex items-center justify-center">
                        <span className="text-xs font-medium text-white">
                          {stream.creator?.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Remove Button Overlay */}
                  {reportId && (
                    <button
                      onClick={() => handleExcludeContent(stream.id)}
                      disabled={removing === stream.id}
                      className="absolute top-2 bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center bg-black/50 hover:bg-red-600/75 disabled:bg-gray-600 rounded-lg"
                      title="Remove from report"
                    >
                      {removing === stream.id ? (
                        <div className="animate-spin">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                        </div>
                      ) : (
                        <X className="w-6 h-6 text-white" />
                      )}
                    </button>
                  )}
                </div>

                {/* Content Details */}
                <div className="flex-1 p-3 flex flex-col space-y-2">
                  {/* Title */}
                  <div>
                    <h3 className="text-xs font-medium text-white line-clamp-2 hover:text-purple-400">
                      {stream.title || 'Untitled Stream'}
                    </h3>
                    <p className="text-xs text-white mt-0.5">
                      {stream.creator?.name || 'Unknown Creator'}
                    </p>
                  </div>

                  {/* Date */}
                  <div className="flex items-center space-x-1 text-xs text-white">
                    <Calendar className="w-2.5 h-2.5" />
                    <span>{formatDate(stream.postDate, 'short')}</span>
                  </div>

                  {/* Streaming Metrics Grid */}
                  {isYouTubeDataStale(stream) && (
                    <div className="flex items-center gap-1 text-xs text-amber-400 bg-amber-500/10 rounded px-2 py-1">
                      <span title="YouTube data >30 days old — refresh to update">&#9888; YouTube data may be outdated</span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 pt-1 border-t border-white/10">
                    {/* Avg CCV */}
                    <div className="space-y-0.5">
                      <div className="flex items-center space-x-0.5">
                        <Users className="w-2.5 h-2.5 text-purple-500" />
                        <span className="text-xs font-medium text-white">Avg CCV</span>
                      </div>
                      <p className="text-xs font-semibold text-white">
                        {formatFullNumber(stream.streamAvgCcv || stream.avgCcv)}
                      </p>
                    </div>

                    {/* Peak CCV */}
                    <div className="space-y-0.5">
                      <div className="flex items-center space-x-0.5">
                        <TrendingUp className="w-2.5 h-2.5 text-green-500" />
                        <span className="text-xs font-medium text-white">Peak CCV</span>
                      </div>
                      <p className="text-xs font-semibold text-white">
                        {formatFullNumber(stream.streamPeakCcv || stream.peakCcv)}
                      </p>
                    </div>

                    {/* Watch Hours */}
                    <div className="space-y-0.5">
                      <div className="flex items-center space-x-0.5">
                        <Clock className="w-2.5 h-2.5 text-sc-pink" />
                        <span className="text-xs font-medium text-white">Watch Hrs</span>
                        <YouTubeEstimateTooltip metric="watchHours" />
                      </div>
                      <p className="text-xs font-semibold text-white">
                        {formatWatchHours(stream.streamWatchHours || stream.watchHours || stream.watchTimeHours)}
                      </p>
                    </div>

                    {/* VOD Views */}
                    <div className="space-y-0.5">
                      <div className="flex items-center space-x-0.5">
                        <Eye className="w-2.5 h-2.5 text-orange-500" />
                        <span className="text-xs font-medium text-white">VOD Views</span>
                      </div>
                      <p className="text-xs font-semibold text-white">
                        {formatFullNumber(stream.vodViews)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Show More Button */}
          {filteredContent.length > itemsToShow && (
            <div className="flex justify-center">
              <button
                onClick={() => setItemsToShow(prev => prev + 10)}
                className="px-4 py-2 hover:bg-white/10 text-white rounded-lg text-sm font-medium transition-colors border border-white"
              >
                Show More ({filteredContent.length - itemsToShow} remaining)
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-white">
          No streams match the current filters
        </div>
      )}
    </div>
  );
};

export default StreamingGridView;
